import Joi from 'joi';
import _ from 'lodash';

import PropTypes from 'prop-types';
import Form from '../../common/form';

import { getDefectTypes, getResultOptionsForDRTReport } from '../../../utils/data';
import { IMAGE_LIMITS, MATERIAL_OPTIONS } from '../../../utils/constants';
import { getFileUploadTypes, matchPhrases } from '../../../utils/utilities';

class DRTReportForm extends Form {
	state = {
		data: {
			weldNumber: '',
			shotNumber: '',
			thickness: 0,
			pipeDia: 0,
			defect: '',
			result: '',
			defectType: '',
			defectLength: '',
			source: '',
			sourceActivity: '',
			sourceSize: '',
			screens: '',
			rtTechnique: '',
			sdd: '',
			odd: '',
			iqi: '',
			comparatorLocation: '',
			comparatorSize: '',
			pipeSpecification: '',
			spatialFrequency: '',
			spatialLinearity: '',
			imageFilmType: '',
			imagingPlateType: '',
			pipeMaterialClass: '',
			drtScannerModalName: '',
			serialNumber: '',
			equipmentSerialNumber: '',
			inspectionDate: new Date(),
			drtScannerCalibrationDate: new Date(),
			sourceConformityInspectionDate: new Date(),
			limitation: '',
			image: [],
		},
		errors: {},
		loading: false,
		progress: 0,
	};

	schema = {
		weldNumber: Joi.string().min(3).required().label('Weld Number'),
		shotNumber: Joi.string().min(3).required().label('DRT Shot Number'),
		thickness: Joi.number().positive().required().label('Thickness (mm)'),
		pipeDia: Joi.number().positive().required().label('Pipe Dia (Inch)'),
		defect: Joi.string().min(3).required().label('Defect'),
        defectType: Joi.alternatives().try(Joi.array().min(1).items(Joi.object({
			id: Joi.string().required(),
			name: Joi.string().required(),
		})), Joi.string().min(3)).required().label('Defect Type'),
		result: Joi.string().min(3).required().label('Result'),
		defectLength: Joi.string().min(3).required().label('Defect Length'),
		source: Joi.string().min(3).required().label('Source'),
		sourceActivity: Joi.string().min(3).required().label('Source Activity'),
		sourceSize: Joi.string().min(3).required().label('Source Size'),
		screens: Joi.string().min(3).required().label('Screens'),
		sdd: Joi.string().min(3).required().label('SDD'),
		iqi: Joi.string().min(3).required().label('IQI'),
		odd: Joi.string().min(3).required().label('ODD'),
		comparatorLocation: Joi.string().min(3).required().label('Comparator Location'),
		comparatorSize: Joi.string().min(3).required().label('Comparator Size'),
		pipeSpecification: Joi.string().min(3).required().label('Material Specification'),
		rtTechnique: Joi.string().min(3).required().label('RT Technique'),
		spatialFrequency: Joi.string().min(3).required().label('Spacial Frequency'),
		spatialLinearity: Joi.string().min(3).required().label('Spatial Linearity'),
		imageFilmType: Joi.string().min(3).required().label('Image Film Type'),
		imagingPlateType: Joi.string().required().label('Imaging Plate Type'),
		pipeMaterialClass: Joi.string().required().label('Pipe Material Class'),
		drtScannerModalName: Joi.string().min(3).required().label('DRT Scanner Name'),
		serialNumber: Joi.string().min(3).required().label('Serial Number'),
		equipmentSerialNumber: Joi.string().required().label('XRay Serial Number'),
		inspectionDate: Joi.date().less('now').required().label('Inspection Date'),
		drtScannerCalibrationDate: Joi.date().greater('now').required().label('DRT Scanner Calibration Due Date'),
		sourceConformityInspectionDate: Joi.date().greater('now').required().label('Source Conformity Inspection Date'),
		limitation: Joi.string().optional().label('Limitation'),
		image: Joi.any().when('result', {
            is: 'Restrictions',
            then: Joi.array().min(0).max(IMAGE_LIMITS.DRT_PHOTO).required().label('DRT Image'),
            otherwise: Joi.array().min(1).max(IMAGE_LIMITS.DRT_PHOTO).required().label('DRT Image'),
        }),
	};

	static propTypes = {
		onAdd: PropTypes.func,
	};

	componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<{ data: any; errors: any; progress: number; loading: boolean; touched?: boolean; }>, snapshot?: any): void {
		if (prevState.data.result !== this.state.data.result) {
			if (this.getResultIsRestriction()) {
				this.setState({
					data: {
						...this.state.data,
						defect: 'N/A',
						defectType: 'N/A',
						defectLength: 'N/A',
					}
				});
			}
		}
	}

	getApiData = () => {
        if (_.isArray(this.state.data.defectType)) {
            const defectType = this.state.data.defectType.map((option) => option.id).join(', ');
            return { ...this.state.data, defectType };
        }

        return this.state.data;
    };

	getResultIsRestriction = () => {
		return matchPhrases(this.state.data.result, 'restrictions');
	};

	doSubmit = (): void => {
		this.props.onAdd(this.getApiData());

		this.setState({
			data: {
				weldNumber: '',
				shotNumber: '',
				thickness: 0,
				pipeDia: 0,
				defect: '',
				defectType: '',
				result: '',
				defectLength: '',
				source: '',
				sourceActivity: '',
				sourceSize: '',
				screens: '',
				rtTechnique: '',
				sdd: '',
				odd: '',
				iqi: '',
				comparatorLocation: '',
				comparatorSize: '',
				pipeSpecification: '',
				spatialFrequency: '',
				spatialLinearity: '',
				imageFilmType: '',
				imagingPlateType: '',
				pipeMaterialClass: '',
				equipmentSerialNumber: '',
				drtScannerModalName: '',
				serialNumber: '',
				inspectionDate: new Date(),
				drtScannerCalibrationDate: new Date(),
				sourceConformityInspectionDate: new Date(),
				limitation: '',
				image: [],
			},
		});
	};

	renderDefectType = () => {
        if (this.state.data.result && matchPhrases(this.state.data.result, 'rejected')) {
            return this.renderMultiSelect({
                name: 'defectType',
                label: 'Defect Type',
                placeholder: 'Select Defect Type',
                showCheckbox: true,
				loading: false,
                options: getDefectTypes().map((type) => ({ id: type, name: type })),
            });
        }

        return this.renderInput({
            type: 'text',
            name: 'defectType',
            label: 'Defect Type',
            placeholder: 'Enter Defect Type',
			disabled: this.getResultIsRestriction(),
        })
    };

	render() {
		return (
			<form autoComplete="off" onSubmit={this.handleSubmit}>
				<div className="row">
					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'weldNumber',
							label: 'Weld Number',
							placeholder: 'Enter Weld Number',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'shotNumber',
							label: 'DRT Shot Number',
							placeholder: 'Enter DRT Shot Number',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'number',
							name: 'thickness',
							label: 'Thickness (mm)',
							placeholder: 'Enter Thickness (mm)',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'number',
							name: 'pipeDia',
							label: 'Pipe Dia (Inch)',
							placeholder: 'Enter Pipe Dia (Inch)',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'defect',
							label: 'Defect',
							placeholder: 'Enter Defect',
							disabled: this.getResultIsRestriction(),
						})}
					</div>

					<div className="col">
						{this.renderDefectType()}
					</div>

					<div className="col">
						{this.renderSelect({
							name: 'result',
							label: 'Result',
							placeholder: 'Select Result',
							options: getResultOptionsForDRTReport(),
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'defectLength',
							label: 'Defect Length (mm)',
							placeholder: 'Enter Defect Length',
							disabled: this.getResultIsRestriction(),
						})}
					</div>
				</div>

				<div className="my-4 divider"></div>

				<div className="row">
					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'source',
							label: 'Source',
							placeholder: 'Enter Source',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'sourceActivity',
							label: 'Source Activity',
							placeholder: 'Enter Source Activity',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'sourceSize',
							label: 'Source Size',
							placeholder: 'Enter Source Size',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'screens',
							label: 'Screens',
							placeholder: 'Enter Screens',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'rtTechnique',
							label: 'RT Technique',
							placeholder: 'Enter RT Technique',
						})}
					</div>
				</div>

				<div className="my-4 divider"></div>

				<div className="row">
					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'sdd',
							label: 'SDD',
							placeholder: 'Enter SDD Value',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'odd',
							label: 'ODD',
							placeholder: 'Enter ODD Value',
						})}
					</div>
					
					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'iqi',
							label: 'IQI',
							placeholder: 'Enter IQI Value',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'comparatorLocation',
							label: 'Comparator Location',
							placeholder: 'Enter Comparator Location',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'comparatorSize',
							label: 'Comparator Size',
							placeholder: 'Enter Comparator Size',
						})}
					</div>

					<div className="col">
						{this.renderSelect({
							options: MATERIAL_OPTIONS,
							name: 'pipeSpecification',
							label: 'Material Specification',
							placeholder: 'Enter Material Specification',
						})}
					</div>
				</div>

				<div className="my-4 divider"></div>

				<div className="row">
					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'spatialFrequency',
							label: 'Spatial Frequency',
							placeholder: 'Enter Spatial Frequency',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'spatialLinearity',
							label: 'Spatial Linearity',
							placeholder: 'Enter Spatial Linearity',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'imageFilmType',
							label: 'Image Film Type',
							placeholder: 'Enter Image Film Type',
						})}
					</div>
				</div>

				<div className="my-4 divider"></div>

				<div className="row">
					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'imagingPlateType',
							label: 'Imaging Plate Type',
							placeholder: 'Enter Imaging Plate Type',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'pipeMaterialClass',
							label: 'Pipe Material Class',
							placeholder: 'Enter Pipe Material Class',
						})}
					</div>
				</div>

				<div className="my-4 divider"></div>

				<div className="row">
					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'equipmentSerialNumber',
							label: 'XRay Serial Number',
							placeholder: 'Enter XRay Serial Number',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'drtScannerModalName',
							label: 'DRT Scanner Name',
							placeholder: 'Enter DRT Scanner Name',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'serialNumber',
							label: 'DRT Scanner Serial Number',
							placeholder: 'Enter DRT Scanner Serial Number',
						})}
					</div>

					<div className="col">
						{this.renderDatePicker({
							name: 'inspectionDate',
							label: 'Inspection Date',
							placeholderText: 'Select Inspection Date',
							maxDate: new Date(),
						})}
					</div>

					<div className="col">
						{this.renderDatePicker({
							name: 'drtScannerCalibrationDate',
							label: 'DRT Scanner Calibration Due Date',
							placeholderText: 'DRT Scanner Calibration Due Date',
							minDate: new Date(),
						})}
					</div>
					
					<div className="col">
						{this.renderDatePicker({
							name: 'sourceConformityInspectionDate',
							label: 'Source Conformity Inspection Date',
							placeholderText: 'Source Conformity Inspection Date',
							minDate: new Date(),
						})}
					</div>
				</div>

				<div className="my-4 divider"></div>

				{this.renderTextArea({
					name: 'limitation',
					placeholder: 'Enter Limitation (If any)',
					label: 'Limitation (If any)',
					rows: '6',
				})}

				<div className="my-4 divider"></div>

				{this.renderFileInput({
					max: IMAGE_LIMITS.DRT_PHOTO,
					multiple: IMAGE_LIMITS.DRT_PHOTO > 1,
					name: 'image',
					accept: 'image/*,application/pdf',
					label: 'DRT Image',
					sublabel: 'Upload DRT Photo',
					types: getFileUploadTypes(),
					enforceImageCaptionUpdate: true
				})}

				<div className="my-4 divider"></div>

				<div className="d-flex justify-content-end">
					{this.renderButton('Add Item')}
				</div>
			</form>
		);
	}
}

export default DRTReportForm;
